import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import TopMenuBar from "components/TopMenuBar";
import FooterSection from "components/FooterSection";
import LeadCaptureModal from "components/LeadCaptureModal";
import { Heading } from "components";
import { EMOTION_COLORS, LABEL_COPY, PROFILE_SAMPLES } from "./profileSamples";
import "./style.css";

function TrendChart({ days }) {
    return (
        <div className="profile-trend">
            {days.map((entry, index) => (
                <div className="profile-trend-col" key={`${entry.day}-${index}`}>
                    <div className="profile-trend-track">
                        <div
                            className="profile-trend-bar"
                            style={{
                                height: `${Math.round(entry.intensity * 100)}%`,
                                background: EMOTION_COLORS[entry.emotion] || "#6ddcff",
                            }}
                            title={`${entry.day}: ${LABEL_COPY[entry.emotion]} (${Math.round(entry.intensity * 100)}%)`}
                        />
                    </div>
                    <span className="profile-trend-day">{entry.day}</span>
                </div>
            ))}
        </div>
    );
}

function DistributionBars({ days }) {
    const distribution = useMemo(() => {
        const counts = {};
        days.forEach((entry) => {
            counts[entry.emotion] = (counts[entry.emotion] || 0) + 1;
        });
        const total = days.length;
        return Object.entries(counts)
            .map(([emotion, count]) => ({ emotion, count, pct: Math.round((count / total) * 100) }))
            .sort((left, right) => right.count - left.count);
    }, [days]);

    return (
        <div className="profile-distribution">
            {distribution.map((entry) => (
                <div className="profile-distribution-row" key={entry.emotion}>
                    <span>{LABEL_COPY[entry.emotion]}</span>
                    <i>
                        <b style={{ width: `${entry.pct}%`, background: EMOTION_COLORS[entry.emotion] || "#6ddcff" }} />
                    </i>
                    <strong>{entry.pct}%</strong>
                </div>
            ))}
        </div>
    );
}

export default function EmotionProfileDemo() {
    const [selectedId, setSelectedId] = useState(PROFILE_SAMPLES[0].id);
    const [modalOpen, setModalOpen] = useState(false);
    const selected = PROFILE_SAMPLES.find((sample) => sample.id === selectedId) || PROFILE_SAMPLES[0];

    return (
        <>
            <Helmet>
                <title>Emotion Profile Tracking — Sample Dashboard — AIHumanity</title>
                <meta
                    name="description"
                    content="See the kind of longitudinal emotional-pattern dashboard AIHumanity's Emotion Profile Tracking product surfaces, using illustrative sample data."
                />
            </Helmet>

            <div className="profile-demo-page">
                <TopMenuBar />

                <section className="profile-demo-hero">
                    <span className="profile-demo-badge">Sample Emotion Profile</span>
                    <Heading size="3xl" as="h1" className="!text-transparent bg-gradient5 bg-clip-text">
                        See your emotional patterns, made visible
                    </Heading>
                    <p>
                        Emotion Profile Tracking turns short voice, expression, or biosignal check-ins into a
                        longitudinal dashboard — the kind of week-over-week pattern a single conversation can't
                        show. Pick a sample below to see what that looks like over two weeks.
                    </p>
                    <div className="profile-demo-disclaimer">
                        <strong>Illustrative sample data</strong>
                        <span>These four profiles are hand-authored examples, not real users' data — a preview of the pattern, not a live tracker.</span>
                    </div>
                </section>

                <section className="profile-demo-picker" aria-label="Sample profiles">
                    {PROFILE_SAMPLES.map((sample) => (
                        <button
                            type="button"
                            key={sample.id}
                            className={`profile-demo-card${selectedId === sample.id ? " active" : ""}`}
                            onClick={() => setSelectedId(sample.id)}
                        >
                            <span className="profile-demo-icon">{sample.icon}</span>
                            <strong>{sample.label}</strong>
                            <p>{sample.description}</p>
                        </button>
                    ))}
                </section>

                <section className="profile-demo-dashboard">
                    <div className="profile-demo-dashboard-head">
                        <span>14-day emotional pattern</span>
                        <strong>{selected.label}</strong>
                    </div>

                    <TrendChart days={selected.days} />

                    <div className="profile-demo-insight">
                        <span>Pattern insight</span>
                        <p>{selected.insight}</p>
                    </div>

                    <div className="profile-demo-dashboard-foot">
                        <span>Emotion distribution over these 14 days</span>
                        <DistributionBars days={selected.days} />
                    </div>
                </section>

                <section className="profile-demo-cta">
                    <div>
                        <strong>Want to see your own patterns?</strong>
                        <span>Emotion Profile Tracking is rolling out in waves. Join the list to be invited.</span>
                    </div>
                    <button type="button" className="profile-demo-cta-button" onClick={() => setModalOpen(true)}>
                        Join the waitlist
                    </button>
                </section>

                <FooterSection />
            </div>

            <LeadCaptureModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                kind="waitlist"
                context="Interested in: Emotion Profile Tracking (sample profile demo)"
            />
        </>
    );
}
